import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCanMessage } from '@/shared/hooks/useCanMessage';
import { useConversations } from '@/shared/hooks/useConversations';
import { cn } from '@/lib/utils';

interface MessageButtonProps {
  targetUserId: string;
  className?: string;
}

export const MessageButton: React.FC<MessageButtonProps> = ({
  targetUserId,
  className,
}) => {
  const navigate = useNavigate();
  const { canMessage, isLoading } = useCanMessage(targetUserId);
  const { createOrGetConversation, isCreating } = useConversations();
  const [error, setError] = useState<string | null>(null);

  if (isLoading || !canMessage) return null;

  const handleClick = async () => {
    setError(null);
    try {
      const convId = await createOrGetConversation(targetUserId);
      navigate(`/messages/${convId}`);
    } catch (err: any) {
      setError(err?.message ?? 'Could not open conversation');
    }
  };

  return (
    <div className={cn('flex flex-col', className)}>
      <button
        onClick={handleClick}
        disabled={isCreating}
        aria-label="Send message"
        className="w-full py-1.5 h-auto flex items-center justify-center gap-2 px-4 rounded-full text-sm font-semibold bg-sossiety-red text-white hover:bg-[#C02010] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
        Message
      </button>

      {error && (
        <p className="text-red-400 text-xs mt-1 text-center">{error}</p>
      )}
    </div>
  );
};
